<script setup lang="ts">
/**
 * PageSettingsFields — generisk felt-renderer for en sides indstillinger
 * (12-08-2026). Renderer felterne fra shared-registry'ets PageSettingsDef
 * (number/boolean/select/ruleSets/rules/campaigns/syncInterval/syncIncremental) mod
 * et values-objekt. Bruges af BÅDE
 * tandhjuls-draweren og Indstillinger → Sideindstillinger, så de to altid
 * renderer ens.
 *
 * Uden redigeringsret (disabled) vises felterne låst — alle med view-adgang
 * må SE indstillingerne, kun edit-grant må ændre dem ([D-141]-semantikken).
 */

import { computed, watch } from 'vue';
import {
  SYNC_NIGHTLY_ONLY_MINUTES,
  describeSyncInterval,
  PAGE_SETTING_RULE_SET_NAME_MAX,
  PAGE_SETTING_RULE_SETS_MAX,
  newPageSettingRuleSetId,
  type PageSettingFieldDef,
  type PageSettingRule,
  type PageSettingRuleSet,
  type PageSettingsDef,
  type PageSettingValue,
} from '@cockpit/shared';

import { useSyncSchedules } from '../composables/useSyncSchedules';
import { ICONS } from '../icons';
import { formatDurationDa, formatEpochDateTimeDa } from '../lib/datetime';
import PageSettingsCampaignPicker from './PageSettingsCampaignPicker.vue';
import PageSettingsRuleList from './PageSettingsRuleList.vue';

const props = defineProps<{
  def: PageSettingsDef;
  values: Record<string, PageSettingValue>;
  disabled: boolean;
}>();

const emit = defineEmits<{
  (e: 'update', key: string, value: PageSettingValue): void;
}>();

function onNumberInput(key: string, ev: Event): void {
  const raw = (ev.target as HTMLInputElement).value;
  const n = Number(raw.replace(',', '.'));
  if (Number.isFinite(n)) emit('update', key, n);
}

// ── ruleSets ([D-293]): navngivne regelsæt, hvert m. tekstregler ────────────
// Kladden lever hos forælderen (draft); her udsendes hele den nye liste ved
// hver ændring, så Gem/Fortryd virker som for de andre felttyper. Et sæt uden
// navn og en regel uden værdi vises med et hint, fordi serveren dropper dem
// tavst ved gem (sanitizePageSettingRuleSets) — brugeren skal se det FØR Gem.
function setsOf(key: string): PageSettingRuleSet[] {
  const v = props.values[key];
  // Arrays kan også være kampagne-nøgler (strenge, [D-297]); kun objekter er regelsæt.
  return Array.isArray(v) ? v.filter((x): x is PageSettingRuleSet => typeof x === 'object' && x !== null) : [];
}
function emitSets(f: PageSettingFieldDef, sets: PageSettingRuleSet[]): void {
  emit('update', f.key, sets);
}
function addSet(f: PageSettingFieldDef): void {
  const sets = setsOf(f.key);
  if (sets.length >= PAGE_SETTING_RULE_SETS_MAX) return;
  emitSets(f, [...sets, { id: newPageSettingRuleSetId(), name: '', rules: [] }]);
}
function removeSet(f: PageSettingFieldDef, si: number): void {
  emitSets(f, setsOf(f.key).filter((_, i) => i !== si));
}
function renameSet(f: PageSettingFieldDef, si: number, name: string): void {
  emitSets(f, setsOf(f.key).map((s, i) => (i === si ? { ...s, name } : s)));
}
function setRules(f: PageSettingFieldDef, si: number, rules: PageSettingRule[]): void {
  emitSets(f, setsOf(f.key).map((s, i) => (i === si ? { ...s, rules } : s)));
}
const inputValue = (ev: Event): string => (ev.target as HTMLInputElement).value;
// [D-476]: en enkel regelliste (objekter med felt, operator og værdi).
function rulesOf(key: string): PageSettingRule[] {
  const v = props.values[key];
  return Array.isArray(v) ? v.filter((x): x is PageSettingRule => typeof x === 'object' && x !== null && 'field' in x) : [];
}

// ── synk-intervaller ([D-356]): status pr. kilde fra /api/page-settings/sync ─
const sync = useSyncSchedules();
const hasSyncFields = computed(() => props.def.fields.some((f) => f.type === 'syncInterval'));
watch(hasSyncFields, (has) => { if (has) void sync.load(); }, { immediate: true });

function syncLines(f: PageSettingFieldDef): { text: string; warn: boolean; at?: number; ms?: number }[] {
  // `at`: tidspunktet bag en relativ dato i linjen, til v-fulldate (§94);
  // `ms`: varigheden i linjen, til v-duration (§100).
  const out: { text: string; warn: boolean; at?: number; ms?: number }[] = [];
  const minutes = Number(props.values[f.key] ?? SYNC_NIGHTLY_ONLY_MINUTES);
  out.push({ text: `Svarer til ${describeSyncInterval(minutes)}.`, warn: false });
  const st = sync.statusFor(f.syncSource);
  if (!st) return out;
  if (!st.active && st.inactiveReason) out.push({ text: st.inactiveReason, warn: true });
  if (st.lastRunAt != null) {
    const took = st.lastRunDurationMs != null ? `, tog ${formatDurationDa(st.lastRunDurationMs)}` : '';
    const failed = st.lastRunOk === false ? ', fejlede' : '';
    out.push({
      // En tekst der selv ender på punktum, får ikke 2.
      text: `Seneste kørsel: ${formatEpochDateTimeDa(st.lastRunAt, { seconds: false })}${took}${failed}`.replace(/\.*$/, '.'),
      warn: st.lastRunOk === false,
      at: st.lastRunAt,
      ms: st.lastRunDurationMs ?? undefined,
    });
  } else {
    out.push({ text: 'Ingen kørsel registreret endnu.', warn: false });
  }
  if (st.typicalDurationMs != null) {
    out.push({
      text: `Typisk ${formatDurationDa(st.typicalDurationMs)} pr. kørsel i løbet af dagen (median af ${st.runsMeasured}).`,
      warn: false,
      ms: st.typicalDurationMs,
    });
  }
  if (st.updatedBy === 'Sync-doktoren' && st.updatedAt != null) {
    out.push({ text: `Sat af Sync-doktoren ${formatEpochDateTimeDa(st.updatedAt, { seconds: false })}.`, warn: false, at: st.updatedAt });
  }
  return out;
}

// ── campaigns ([D-297]): kampagner valgt på platformens ID ──────────────────
function campaignsOf(key: string): string[] {
  const v = props.values[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-for="f in props.def.fields" :key="f.key" class="flex flex-col gap-1">
      <label :for="`ps-${props.def.routePath}-${f.key}`" class="text-xs font-medium text-ink">
        {{ f.label }}
      </label>

      <div v-if="f.type === 'number'" class="flex items-center gap-2">
        <input
          :id="`ps-${props.def.routePath}-${f.key}`"
          type="number"
          :min="f.min"
          :max="f.max"
          :step="f.step ?? 1"
          :value="Number(props.values[f.key] ?? 0)"
          :disabled="props.disabled"
          class="px-2.5 py-1.5 rounded-lg border border-line bg-surface text-sm text-ink w-28 tabular-nums focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
          @input="onNumberInput(f.key, $event)"
        />
        <span v-if="f.unit" class="text-xs text-muted">{{ f.unit }}</span>
      </div>

      <label
        v-else-if="f.type === 'boolean'"
        class="flex items-center gap-2 text-sm text-ink"
        :class="props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
      >
        <input
          :id="`ps-${props.def.routePath}-${f.key}`"
          type="checkbox"
          :checked="Boolean(props.values[f.key])"
          :disabled="props.disabled"
          class="rounded border-line"
          @change="emit('update', f.key, ($event.target as HTMLInputElement).checked)"
        />
        Til
      </label>

      <!-- text ([D-371]): kort tekstværdi (fx liste-id/dato) m. mønster-værn;
           serveren beholder default hvis værdien ikke matcher feltets mønster. -->
      <input
        v-else-if="f.type === 'text'"
        :id="`ps-${props.def.routePath}-${f.key}`"
        type="text"
        :value="String(props.values[f.key] ?? '')"
        :maxlength="f.maxLength ?? 200"
        :placeholder="f.placeholder"
        :disabled="props.disabled"
        class="px-2.5 py-1.5 rounded-lg border border-line bg-surface text-sm text-ink max-w-sm focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
        @input="emit('update', f.key, ($event.target as HTMLInputElement).value)"
      />

      <!-- select ([D-285]): ét valg blandt faste muligheder, gemt som streng. -->
      <select
        v-else-if="f.type === 'select'"
        :id="`ps-${props.def.routePath}-${f.key}`"
        :value="String(props.values[f.key] ?? '')"
        :disabled="props.disabled"
        class="px-2.5 py-1.5 rounded-lg border border-line bg-surface text-sm text-ink max-w-sm focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
        @change="emit('update', f.key, ($event.target as HTMLSelectElement).value)"
      >
        <option v-for="o in f.options ?? []" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>

      <!-- ruleSets ([D-293]): navngivne regelsæt, hvert m. tekstregler "felt ·
           operator · værdi". Kompakt nok til drawerens 300 px: felt + operator
           på én linje, værdi + fjern-knap på den næste. -->
      <div v-else-if="f.type === 'ruleSets'" class="flex flex-col gap-3" :data-testid="`ps-rulesets-${f.key}`">
        <div
          v-for="(s, si) in setsOf(f.key)"
          :key="s.id"
          class="flex flex-col gap-2 p-2 rounded-lg border border-line"
          data-testid="ps-rule-set"
          :data-set-id="s.id"
        >
          <div class="flex items-center gap-1 min-w-0">
            <input
              type="text"
              :value="s.name"
              :maxlength="PAGE_SETTING_RULE_SET_NAME_MAX"
              :disabled="props.disabled"
              :aria-label="`Regelsæt ${si + 1}: navn`"
              placeholder="Navn på regelsættet"
              class="flex-1 min-w-0 px-2 py-1 rounded-lg border border-line bg-surface text-sm font-medium text-ink focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ps-rule-set-name"
              @input="renameSet(f, si, inputValue($event))"
            />
            <button
              v-if="!props.disabled"
              type="button"
              class="w-7 h-7 shrink-0 inline-flex items-center justify-center rounded-lg text-muted hover:bg-hover hover:text-ink transition-colors"
              :aria-label="`Fjern regelsæt ${si + 1}`"
              v-tip="`Fjern hele regelsættet${s.name ? ` ${s.name}` : ''} med alle dets regler.`"
              data-testid="ps-rule-set-remove"
              @click="removeSet(f, si)"
            >
              <span class="block w-3.5 h-3.5" v-html="ICONS.close" />
            </button>
          </div>
          <p v-if="!s.name.trim()" class="text-[11px] text-red">Skriv et navn. Et regelsæt uden navn gemmes ikke.</p>
          <p class="text-[11px] text-faint">{{ s.rules.length }} {{ s.rules.length === 1 ? 'regel' : 'regler' }}. En vare rammes når mindst 1 af dem passer.</p>

          <PageSettingsRuleList
            :rules="s.rules"
            :rule-fields="f.ruleFields ?? []"
            :disabled="props.disabled"
            :label-prefix="`Regelsæt ${si + 1}, `"
            @update="(rules) => setRules(f, si, rules)"
          />
        </div>
        <p v-if="setsOf(f.key).length === 0" class="text-[11px] text-faint">Ingen regelsæt.</p>
        <button
          v-if="!props.disabled"
          type="button"
          :disabled="setsOf(f.key).length >= PAGE_SETTING_RULE_SETS_MAX"
          class="self-start px-2.5 py-1 rounded-lg border border-line text-xs text-ink hover:bg-hover transition-colors disabled:opacity-40"
          data-testid="ps-rule-set-add"
          @click="addSet(f)"
        >
          Tilføj regelsæt
        </button>
      </div>

      <!-- rules ([D-476]): ÉN regelliste uden navn og valg; alle reglerne gælder altid. -->
      <div v-else-if="f.type === 'rules'" class="flex flex-col gap-2" :data-testid="`ps-rules-${f.key}`">
        <p class="text-[11px] text-faint">{{ rulesOf(f.key).length }} {{ rulesOf(f.key).length === 1 ? 'regel' : 'regler' }}. En vare rammes når mindst 1 af dem passer.</p>
        <PageSettingsRuleList
          :rules="rulesOf(f.key)"
          :rule-fields="f.ruleFields ?? []"
          :disabled="props.disabled"
          label-prefix=""
          @update="(rules) => emit('update', f.key, rules)"
        />
      </div>

      <!-- campaigns ([D-297]): annoncekampagner valgt på ID, aldrig på navn. -->
      <PageSettingsCampaignPicker
        v-else-if="f.type === 'campaigns'"
        :route-path="props.def.routePath"
        :field="f"
        :value="campaignsOf(f.key)"
        :disabled="props.disabled"
        @update="(v) => emit('update', f.key, v)"
      />

      <!-- syncInterval ([D-356]): minutter mellem synkroniseringerne med en 3. part. -->
      <div v-else-if="f.type === 'syncInterval'" class="flex items-center gap-2" :data-testid="`ps-sync-${f.syncSource}`">
        <input
          :id="`ps-${props.def.routePath}-${f.key}`"
          type="number"
          :min="f.min"
          :max="f.max"
          step="1"
          :value="Number(props.values[f.key] ?? SYNC_NIGHTLY_ONLY_MINUTES)"
          :disabled="props.disabled"
          class="px-2.5 py-1.5 rounded-lg border border-line bg-surface text-sm text-ink w-28 tabular-nums focus:outline-none focus:border-ink/40 disabled:opacity-50 disabled:cursor-not-allowed"
          @input="onNumberInput(f.key, $event)"
        />
        <span class="text-xs text-muted">{{ f.unit ?? 'min' }}</span>
      </div>

      <!-- syncIncremental ([D-356]): kun det der er ændret, i kørslerne i løbet af dagen. -->
      <label
        v-else-if="f.type === 'syncIncremental'"
        class="flex items-center gap-2 text-sm text-ink"
        :class="props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
      >
        <input
          :id="`ps-${props.def.routePath}-${f.key}`"
          type="checkbox"
          :checked="Boolean(props.values[f.key])"
          :disabled="props.disabled"
          class="rounded border-line"
          @change="emit('update', f.key, ($event.target as HTMLInputElement).checked)"
        />
        Til
      </label>

      <p v-if="f.help" class="text-[11px] text-faint max-w-sm">{{ f.help }}</p>
      <template v-if="f.type === 'syncInterval'">
        <p
          v-for="(l, li) in syncLines(f)"
          :key="li"
          class="text-[11px] max-w-sm"
          :class="l.warn ? 'text-amber' : 'text-muted'"
          data-testid="ps-sync-status"
          v-fulldate="l.at"
          v-duration="l.ms"
        >{{ l.text }}</p>
      </template>
    </div>
  </div>
</template>
